import { Matrix, Point } from "pixi.js";

import BaseEntity from "../entity/BaseEntity";
import Entity from "../entity/Entity";
import { lerpOrSnap } from "../util/MathUtil";
import { V, V2d } from "../Vector";
import { GameRenderer2d } from "./GameRenderer2d";
import { LayerInfo } from "./LayerInfo";

export interface CameraViewport {
  top: number;
  bottom: number;
  left: number;
  right: number;
  width: number;
  height: number;
}

/**
 * 2D Camera system that manages viewport transformations and coordinate space conversions.
 *
 * ## Core Responsibilities
 *
 * The Camera2d handles all aspects of the game's viewport:
 * - **Position & Movement**: Tracks camera position in world space with velocity-based movement
 * - **Zoom Control**: Manages zoom level (z property) for scaling the view
 * - **Coordinate Transformations**: Converts between world space and screen space coordinates
 * - **Layer Management**: Applies different transformations to layers (e.g., parallax effects)
 * - **Smooth Operations**: Provides interpolated movement and zoom for cinematic effects
 *
 * ## Coordinate Space Transformations
 *
 * The camera manages three coordinate spaces:
 *
 * - **World Space**: Game world coordinates in meters (physics space)
 *   - Origin typically at world center
 *   - Used for entity positions, physics calculations
 *   - Example: Player at position [10.5, -3.2] meters
 *
 * - **Screen Space**: Pixel coordinates relative to canvas top-left
 *   - Origin at top-left corner (0,0)
 *   - Used for UI positioning, mouse coordinates
 *   - Example: Mouse click at [640, 360] pixels
 *
 * - **Layer Space**: Screen space with parallax/anchor modifications
 *   - Each layer can have different transformation properties
 *   - Used internally for layer positioning
 *
 * ## Camera Properties
 *
 * - **position**: Camera center in world coordinates [x, y]
 * - **z**: Zoom level (higher = more zoomed in, default ~25)
 * - **angle**: Camera rotation in radians (rarely used)
 * - **velocity**: Current movement velocity for smooth motion
 *
 * ## Common Usage Patterns
 *
 * @example
 * ```typescript
 * // Center camera on player position
 * camera.center(player.getPosition());
 *
 * // Smooth camera following
 * camera.smoothCenter(player.getPosition(), player.velocity, 2.0, 0.8);
 *
 * // Convert mouse click to world position
 * const worldPos = camera.toWorld([mouseX, mouseY]);
 *
 * // Get viewport bounds for culling
 * const viewport = camera.getWorldViewport();
 * if (entity.x > viewport.left && entity.x < viewport.right) {
 *   // Entity is visible, update it
 * }
 *
 * // Smooth zoom for dramatic effect
 * camera.smoothZoom(50); // Zoom in
 * camera.smoothZoom(15); // Zoom out
 * ```
 */
export class Camera2d extends BaseEntity implements Entity {
  tags = ["camera"];
  persistenceLevel = 100;

  renderer: GameRenderer2d;
  position: V2d;
  z: number;
  angle: number;
  velocity: V2d;

  paralaxScale = 0.1;

  constructor(
    renderer: GameRenderer2d,
    position: V2d = V([0, 0]),
    z = 25.0,
    angle = 0,
  ) {
    super();
    this.renderer = renderer;
    this.position = position;
    this.z = z;
    this.angle = angle;
    this.velocity = V([0, 0]);
  }

  get x() {
    return this.position[0];
  }

  set x(value) {
    this.position[0] = value;
  }

  get y() {
    return this.position[1];
  }

  set y(value) {
    this.position[1] = value;
  }

  get vx() {
    return this.velocity[0];
  }

  set vx(value) {
    this.velocity[0] = value;
  }

  get vy() {
    return this.velocity[1];
  }

  set vy(value) {
    this.velocity[1] = value;
  }

  getPosition() {
    return this.position;
  }

  onTick(dt: number) {
    this.x += this.vx * dt;
    this.y += this.vy * dt;
  }

  /**
   * Instantly centers the camera on a world position.
   *
   * @param position - World coordinates [x, y] to center the camera on
   * @example
   * ```typescript
   * // Center on player immediately
   * camera.center(player.getPosition());
   *
   * // Center on specific world location
   * camera.center([0, 0]); // World origin
   * ```
   */
  center([x, y]: V2d) {
    this.x = x;
    this.y = y;
  }

  /**
   * Smoothly moves the camera toward a target position using spring dynamics.
   *
   * This creates natural camera following behavior with configurable responsiveness.
   * The camera will accelerate toward the target and can optionally match target velocity.
   *
   * @param position - Target world coordinates [x, y] to move toward
   * @param velocity - Target velocity [vx, vy] to match (default: [0, 0])
   * @param stiffness - How quickly to accelerate toward target (default: 1.0)
   * @param damping - How much to resist motion, controls overshoot (default: 1.0)
   *
   * @example
   * ```typescript
   * // Basic smooth following
   * camera.smoothCenter(player.getPosition());
   *
   * // Advanced following with velocity matching
   * camera.smoothCenter(
   *   player.getPosition(),
   *   player.velocity,
   *   3.0,  // High stiffness = responsive
   *   0.7   // Lower damping = slight overshoot
   * );
   *
   * // Cinematic slow follow
   * camera.smoothCenter(target, [0, 0], 0.5, 2.0);
   * ```
   */
  smoothCenter(
    [x, y]: V2d,
    [vx, vy]: V2d = V([0, 0]),
    stiffness: number = 1.0,
    damping: number = 1.0,
  ) {
    const dx = x - this.x;
    const dy = y - this.y;

    const dt = this.game!.averageDt;

    this.vx += (stiffness * dx - damping * (this.vx - vx)) * dt;
    this.vy += (stiffness * dy - damping * (this.vy - vy)) * dt;
  }

  smoothSetVelocity([vx, vy]: V2d, stiffness: number = 0.9) {
    this.vx = lerpOrSnap(this.vx, vx, stiffness, 0.001);
    this.vy = lerpOrSnap(this.vy, vy, stiffness, 0.001);
  }

  /**
   * Smoothly interpolates the camera zoom toward a target level.
   *
   * @param z - Target zoom level (higher values = more zoomed in)
   * @param smooth - Interpolation factor (0.0 = instant, 1.0 = no change)
   *
   * @example
   * ```typescript
   * // Smooth zoom in for close-up
   * camera.smoothZoom(50, 0.95);
   *
   * // Quick zoom out for overview
   * camera.smoothZoom(10, 0.7);
   *
   * // Instant zoom (no smoothing)
   * camera.z = 25;
   * ```
   */
  smoothZoom(z: number, smooth: number = 0.9) {
    this.z = smooth * this.z + (1 - smooth) * z;
  }

  /**
   * Gets the current viewport size in logical pixels.
   *
   * Accounts for device pixel ratio to return the actual drawable area size.
   *
   * @returns [width, height] in logical pixels
   */
  getViewportSize(): V2d {
    return V(
      this.renderer.canvas.width / this.renderer.app.renderer.resolution,
      this.renderer.canvas.height / this.renderer.app.renderer.resolution,
    );
  }

  /**
   * Calculates the world coordinate bounds of the current camera viewport.
   * Useful for culling, bounds checking, and viewport-relative positioning.
   */
  getWorldViewport(): CameraViewport {
    const [left, top] = this.toWorld(V(0, 0));
    const [width, height] = this.getViewportSize();
    const [right, bottom] = this.toWorld(V(width, height));
    const worldWidth = right - left;
    const worldHeight = bottom - top;
    return { top, bottom, left, right, width: worldWidth, height: worldHeight };
  }

  /**
   * Checks if a world point is visible within the current camera viewport.
   */
  worldPointIsVisible(point: V2d): boolean {
    return pointInViewport(point, this.getWorldViewport());
  }

  /**
   * Transforms screen pixel coordinates to world space coordinates.
   *
   * Essential for converting UI interactions (mouse clicks, touch) into game world positions.
   * Accounts for camera position, zoom, rotation, and optional parallax.
   *
   * @param screenPos - Screen coordinates [x, y] in pixels from top-left
   * @param parallax - Parallax multiplier [x, y] (default: [1, 1] = no parallax)
   * @returns World coordinates [x, y] in meters
   *
   * @example
   * ```typescript
   * // Convert mouse click to world position
   * const worldPos = camera.toWorld([event.clientX, event.clientY]);
   *
   * // Spawn entity at click location
   * const entity = new Enemy();
   * entity.setPosition(worldPos);
   *
   * // Convert with parallax for background layer
   * const bgWorldPos = camera.toWorld([mouseX, mouseY], [0.5, 0.5]);
   * ```
   */
  toWorld([x, y]: V2d, parallax = V(1.0, 1.0)): V2d {
    let p = new Point(x, y);
    p = this.getMatrix(parallax).applyInverse(p, p);
    return V(p.x, p.y);
  }

  /**
   * Transforms world space coordinates to screen pixel coordinates.
   *
   * Useful for positioning UI elements relative to world objects or for culling.
   * Accounts for camera position, zoom, rotation, and optional parallax.
   *
   * @param worldPos - World coordinates [x, y] in meters
   * @param parallax - Parallax multiplier [x, y] (default: [1, 1] = no parallax)
   * @returns Screen coordinates [x, y] in pixels from top-left
   *
   * @example
   * ```typescript
   * // Position UI element above player
   * const screenPos = camera.toScreen(player.getPosition());
   * healthBar.style.left = screenPos[0] + 'px';
   * healthBar.style.top = (screenPos[1] - 50) + 'px';
   *
   * // Check if entity is on screen
   * const screenPos = camera.toScreen(entity.getPosition());
   * const [viewWidth, viewHeight] = camera.getViewportSize();
   * const isVisible = screenPos[0] >= 0 && screenPos[0] <= viewWidth &&
   *                   screenPos[1] >= 0 && screenPos[1] <= viewHeight;
   * ```
   */
  toScreen([x, y]: V2d, parallax = V(1.0, 1.0)): V2d {
    let p = new Point(x, y);
    p = this.getMatrix(parallax).apply(p, p);
    return V(p.x, p.y);
  }

  /**
   * Creates a transformation matrix for converting world coordinates to screen coordinates.
   *
   * This is the core of the camera's coordinate transformation system. The matrix combines:
   * 1. Translation by camera position
   * 2. Scaling by zoom level and parallax
   * 3. Rotation by camera angle
   * 4. Final translation to center on screen
   *
   * @param parallax - Parallax multiplier [px, py] for this transformation
   * @param anchor - Anchor point [ax, ay] for rotation/scaling origin
   * @returns Pixi transformation matrix
   *
   * @internal This method is primarily used internally by the rendering system.
   */
  getMatrix(
    [px, py]: [number, number] = [1, 1],
    [ax, ay]: V2d = V(0, 0),
  ): Matrix {
    const [w, h] = this.getViewportSize();
    const { x: cx, y: cy, z, angle } = this;

    return (
      new Matrix()
        // align the anchor with the camera
        .translate(ax * px, ay * py)
        .translate(-cx * px, -cy * py)
        // do all the scaling and rotating
        .scale(z * px, z * py)
        .rotate(angle)
        // put it back
        .translate(-ax * z, -ay * z)
        .scale(1 / px, 1 / py)
        // Put it on the center of the screen
        .translate(w / 2.0, h / 2.0)
    );
  }

  /**
   * Applies camera transformations to a rendering layer.
   *
   * Called automatically during the render cycle for each layer. Updates the layer's
   * Pixi container with the appropriate transformation matrix based on the layer's
   * parallax and anchor settings.
   *
   * @param layer - LayerInfo containing the container and transformation properties
   *
   * @internal This method is called automatically by the rendering system.
   */
  updateLayer(layer: LayerInfo) {
    const container = layer.container;
    if (!layer.paralax.equals([0, 0])) {
      const matrix = this.getMatrix(layer.paralax, layer.anchor);
      container.updateTransform({
        x: matrix.tx,
        y: matrix.ty,
        scaleX: matrix.a,
        scaleY: matrix.d,
        skewX: matrix.b,
        skewY: matrix.c,
      });
    }
  }
}

/**
 * Determines whether a given 2D point is within the specified camera viewport.
 *
 * @param point - The 2D point to check, represented as a `V2d` object.
 * @param viewport - The camera viewport, represented as a `CameraViewport` object,
 *   which defines the boundaries (`left`, `right`, `top`, `bottom`).
 * @returns `true` if the point is inside the viewport boundaries; otherwise, `false`.
 */
export function pointInViewport(point: V2d, viewport: CameraViewport): boolean {
  return (
    point.x >= viewport.left &&
    point.x <= viewport.right &&
    point.y >= viewport.top &&
    point.y <= viewport.bottom
  );
}
